<ng-container *transloco="let t">
  <form [formGroup]="form" class="flex w-full flex-col">
    <IRadioGroup
      name="algoMode"
      class="flex w-full flex-col gap-2"
      [value]="form.controls.algoMode.value"
      [disabled]="_disabled()"
      (valueChange)="changeAlgoMode($event)"
    >
      @for (mode of displayedAlgoModes(); track mode) {
        <label
          iLabel
          type="card"
          class="w-full"
          [for]="mode"
          [disabled]="_disabled()"
          [attr.data-testid]="'algo-mode-selection-' + mode"
        >
          <div>
            <span class="type-body-medium">{{ t(AlgoModeStrKey[mode].title) }}</span>
            <p class="text-muted-foreground m-0">{{ t(AlgoModeStrKey[mode].description) }}</p>
          </div>
          <IRadio [id]="mode" [value]="mode" size="lg" [disabled]="_disabled()" [class.hidden]="_targetOnlyMode()" />

          <div content class="mt-5 flex flex-col gap-4 border-t px-2 pt-5 pb-1">
            @if (mode === AlgoMode.ACOS_TARGET) {
              <IFormField>
                <label iLabel>{{ t("algo-mode-selection.acos_target") }}</label>
                <div iInputGroup>
                  <input
                    iInputGroupInput
                    [formControl]="form.controls.acosTarget"
                    data-testid="acos-target"
                    autofocus
                    type="number"
                    [min]="1"
                    [max]="200"
                    #acosTargetInput
                  />
                  <span class="text-subtle-foreground">%</span>
                </div>
                <IError>{{ t(form.controls.acosTarget.errors?.["acosTarget"]) }}</IError>
              </IFormField>

              @if (_targetOnlyMode()) {
                <ng-container *ngTemplateOutlet="dailyBudgetInput"></ng-container>
              }
            } @else if (mode === AlgoMode.TACOS_TARGET) {
              <IFormField>
                <label iLabel>{{ t("algo-mode-selection.tacos_target") }}</label>
                <div iInputGroup>
                  <input
                    iInputGroupInput
                    [formControl]="form.controls.tacosTarget"
                    data-testid="tacos-target"
                    autofocus
                    type="number"
                    [min]="1"
                    [max]="200"
                    #tacosTargetInput
                  />
                  <span class="text-subtle-foreground">%</span>
                </div>
                <IError>{{ t(form.controls.tacosTarget.errors?.["tacosTarget"]) }}</IError>
              </IFormField>
            } @else if (mode === AlgoMode.MONTHLY_BUDGET_TARGET) {
              @if (_targetOnlyMode()) {
                <span class="type-caption-medium text-foreground block">
                  {{ t("strategy-page.current_spend") }}
                </span>
                <app-spend-progress-bar
                  class="w-full"
                  [spend]="currentMonthSpend()!"
                  [budget]="form.controls.monthlyBudget.value!"
                  [endDate]="endOfCurrentMonth()!"
                  [currencyCode]="currencyCode()"
                  [locale]="locale()!"
                />
              }

              <IFormField>
                <label iLabel>{{ t("overview-grid.monthly_budget") }}</label>
                <div iInputGroup>
                  <input
                    iInputGroupInput
                    [formControl]="form.controls.monthlyBudget"
                    data-testid="monthly-budget"
                    type="number"
                    [min]="1"
                    autofocus
                    #monthlyBudgetInput
                  />
                  <span class="text-subtle-foreground" iInputGroupSuffix>
                    <app-budget-currency-badge
                      [budgetCurrency]="currencyCode()"
                      [amount]="form.controls.monthlyBudget.value"
                    />
                  </span>
                </div>
                <IError>{{ t(form.controls.monthlyBudget.errors?.["monthlyBudget"]) }}</IError>
              </IFormField>

              @if (_targetOnlyMode()) {
                <div class="flex items-end gap-2">
                  <IFormField>
                    <label iLabel>{{ t("switch-target-algo-modal.next_monthly_budget") }}</label>
                    <div iInputGroup>
                      <input iInputGroupInput [formControl]="form.controls.nextMonthlyBudget" type="number" />
                      <span iInputGroupSuffix>
                        <app-budget-currency-badge
                          [budgetCurrency]="currencyCode()"
                          [amount]="form.controls.nextMonthlyBudget.value"
                        />
                      </span>
                      <button
                        iButton
                        iInputGroupSuffix
                        size="sm"
                        variant="secondary"
                        [iTooltip]="t('switch-target-algo-modal.keep_mothly_budget_tooltip')"
                        (click)="form.controls.nextMonthlyBudget.setValue(form.controls.monthlyBudget.value)"
                        #nextMonthlyBudgetInput
                      >
                        <IIcon name="undo2" />
                      </button>
                    </div>
                    <span class="type-caption text-muted-foreground">{{ nextMonth() }}</span>
                  </IFormField>
                </div>
              }

              @if (form.controls.monthlyBudget.value && form.controls.monthlyBudget.value > 0) {
                <app-monthly-budget-warning
                  [value]="form.controls.monthlyBudget.value"
                  [currencyCode]="currencyCode()"
                  [spend]="currentMonthSpend()"
                />
              }
            } @else if (mode === AlgoMode.PRODUCT_LAUNCH) {
              <IFormField>
                <label iLabel>{{ t("switch-target-algo-modal.suggested_bid") }}</label>
                <div iInputGroup>
                  <input
                    iInputGroupInput
                    [formControl]="form.controls.suggestedBid"
                    data-testid="product-launch-bid"
                    type="decimal"
                    [min]="0"
                    #suggestedBidInput
                  />
                  <span class="text-subtle-foreground" iInputGroupSuffix>
                    <app-budget-currency-badge
                      [budgetCurrency]="currencyCode()"
                      [amount]="form.controls.suggestedBid.value"
                    />
                  </span>
                </div>
                <IError data-testid="product-launch-bid-error"
                  >{{ t(form.controls.suggestedBid.errors?.["suggestedBid"], [this.minAllowedBid()!]) }}
                </IError>
              </IFormField>

              <ng-container *ngTemplateOutlet="dailyBudgetInput"></ng-container>
            } @else if (mode === AlgoMode.AMAZON_MANAGED_BUDGET) {
              <IFormField>
                <label iLabel>{{ t("common.budget") }}</label>
                <div iInputGroup>
                  <input
                    iInputGroupInput
                    [formControl]="form.controls.budget"
                    type="number"
                    [min]="1"
                    autofocus
                    #budgetInput
                  />
                  <span class="text-subtle-foreground" iInputGroupSuffix>
                    <app-budget-currency-badge
                      [budgetCurrency]="currencyCode()"
                      [amount]="form.controls.budget.value"
                    />
                  </span>
                </div>
                <IError>{{ t(form.controls.budget.errors?.["budget"]) }}</IError>
              </IFormField>
              @if (flightStatus() === "active") {
                <IFormField>
                  <label iLabel>{{ t("algo-mode-selection.start_date") }}</label>
                  <div class="type-body text-muted-foreground">
                    {{ form.controls.budgetDates.value?.start?.toISODate() | date: "shortDate" }}
                  </div>
                </IFormField>
                <IFormField>
                  <label iLabel>{{ t("algo-mode-selection.end_date") }}</label>
                  <IDatePicker
                    [dateFormat]="dateFormat()"
                    [minDate]="now"
                    [maxDate]="nextYear"
                    [range]="false"
                    [withShortcuts]="false"
                    [selectedDateRange]="
                      form.controls.budgetDates.value?.end
                        ? {
                            start: form.controls.budgetDates.value!.end!,
                            end: form.controls.budgetDates.value!.end!,
                          }
                        : null
                    "
                    (selectedDateRangeChange)="selectEndDate($event)"
                  ></IDatePicker>
                </IFormField>
              } @else {
                <IFormField>
                  <label iLabel>{{ t("common.dates") }}</label>
                  <IDatePicker
                    [dateFormat]="dateFormat()"
                    [minDate]="now"
                    [maxDate]="nextYear"
                    [withShortcuts]="false"
                    [selectedDateRange]="form.controls.budgetDates.value ?? null"
                    (selectedDateRangeChange)="selectBudgetDates($event)"
                  ></IDatePicker>
                </IFormField>
                @if (form.controls.budgetDates.errors?.["budgetDates"]; as budgetDatesError) {
                  <IError>{{ t(budgetDatesError) }}</IError>
                }
              }
            }
          </div>
        </label>
      }
    </IRadioGroup>
  </form>

  <ng-template #dailyBudgetInput>
    <div class="flex w-full items-end gap-2">
      <IFormField class="w-full">
        <label iLabel>{{
          t("switch-target-algo-modal.average_daily_budget", {
            avg: form.controls.algoMode.value === AlgoMode.ACOS_TARGET && _targetOnlyMode(),
          })
        }}</label>
        <div iInputGroup>
          <input
            iInputGroupInput
            [formControl]="form.controls.dailyBudget"
            data-testid="product-launch-budget"
            type="number"
            [min]="0"
          />
          <span class="text-subtle-foreground" iInputGroupSuffix>
            <app-budget-currency-badge [budgetCurrency]="currencyCode()" [amount]="form.controls.dailyBudget.value" />
          </span>
          @if (form.controls.algoMode.value === AlgoMode.ACOS_TARGET && _targetOnlyMode()) {
            <button
              iButton
              iInputGroupSuffix
              size="icon-sm"
              variant="secondary"
              [iTooltip]="t('switch-target-algo-modal.delete_daily_budget')"
              [disabled]="!form.controls.dailyBudget.value"
              (click)="deleteDailyBudget()"
            >
              <IIcon name="trash" />
            </button>
          }
        </div>
        <IError data-testid="product-launch-budget-error">{{
          t(form.controls.dailyBudget.errors?.["dailyBudget"], [this.dailyBudgetLowerBound()!])
        }}</IError>
      </IFormField>
    </div>
  </ng-template>
</ng-container>
